import { useCallback, useEffect, useRef, useState } from 'react';
import { IconClose } from '@indipia/ui-core';
import { CustomerImg, useIsPreview } from './PreviewContext';

export interface ViewerPhoto {
  /** 목록용 썸네일(저용량) — 원본이 올 때까지 흐리게 먼저 보여준다 */
  thumb: string | null;
  /** 원본 */
  src: string | null;
  caption: string;
  sub?: string;
}

interface Props {
  photos: ViewerPhoto[];
  index: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
  /** 현재 사진 저장 — 주면 상단 우측에 저장 버튼을 그린다(미리보기·저장 불가 사진은 주지 않는다). */
  onDownload?: (index: number) => void;
}

function DownloadIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

const SWIPE_X = 40; // 가로 40px 이상 이동 시 넘기기
const SWIPE_DOWN_CLOSE = 90; // 아래로 90px 이상 이동 시 닫기
const DOUBLE_TAP_MS = 300;

function Chevron({ dir }: { dir: 'left' | 'right' }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points={dir === 'left' ? '15 18 9 12 15 6' : '9 18 15 12 9 6'} />
    </svg>
  );
}

/**
 * 사진 확대 뷰어(전체화면) — 화면기획서 "사진 확대보기".
 * 넘기기: 좌우 스와이프·화살표·키보드 ←→(순환 없음), 확대: 더블탭 2배(확대 중엔 넘기지 않음),
 * 닫기: ×·아래로 스와이프·Esc·휴대폰 뒤로가기(history 한 칸을 추가해 페이지 이탈 없이 뷰어만 닫음).
 * 로딩: 썸네일을 흐리게 먼저 → 원본 도착 시 선명하게, 앞뒤 1장은 미리 불러온다.
 */
export function PhotoViewer({ photos, index, onIndexChange, onClose, onDownload }: Props) {
  const preview = useIsPreview();
  const [zoom, setZoom] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const pushedRef = useRef(false);
  const touchRef = useRef<{ x: number; y: number } | null>(null);
  const lastTapRef = useRef(0);
  const stripRef = useRef<HTMLDivElement>(null);

  const total = photos.length;
  const current = photos[index];

  // 뷰어를 열 때 history 한 칸 추가 → 뒤로가기는 뷰어만 닫는다. ×로 닫을 땐 추가한 기록을 되돌린다.
  useEffect(() => {
    // 개발 모드 StrictMode는 이펙트를 두 번 돌리므로 이미 추가했으면 다시 쌓지 않는다.
    if (!pushedRef.current) {
      try {
        window.history.pushState({ photoViewer: true }, '');
        pushedRef.current = true;
      } catch {
        pushedRef.current = false;
      }
    }
    const onPop = () => {
      pushedRef.current = false;
      onClose();
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const close = useCallback(() => {
    if (pushedRef.current) {
      pushedRef.current = false;
      window.history.back(); // popstate가 onClose를 부른다
    } else {
      onClose();
    }
  }, [onClose]);

  const step = useCallback(
    (delta: number) => {
      const next = index + delta;
      if (next < 0 || next >= total) return;
      onIndexChange(next);
    },
    [index, total, onIndexChange],
  );

  // 사진이 바뀌면 확대 해제·로딩 상태 초기화
  useEffect(() => {
    setZoom(false);
    setLoaded(false);
  }, [index]);

  // 앞뒤 1장 미리 불러오기
  useEffect(() => {
    if (preview) return; // 미리보기는 <img> 가 로그인 토큰을 못 실어 미리 불러오기가 의미 없다(CustomerImg 가 blob 으로 받는다)
    [index - 1, index + 1].forEach((i) => {
      const url = photos[i]?.src;
      if (url) new Image().src = url;
    });
  }, [index, photos, preview]);

  // 키보드: ← → 넘기기, Esc 닫기
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') step(-1);
      else if (e.key === 'ArrowRight') step(1);
      else if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [step, close]);

  // 배경 스크롤 잠금
  useEffect(() => {
    const saved = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = saved;
    };
  }, []);

  // 현재 썸네일을 스트립 가운데로
  useEffect(() => {
    const el = stripRef.current?.querySelector<HTMLElement>('[data-active="true"]');
    el?.scrollIntoView?.({ inline: 'center', block: 'nearest' });
  }, [index]);

  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    touchRef.current = { x: t.clientX, y: t.clientY };
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const start = touchRef.current;
    touchRef.current = null;
    if (!start) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;

    // 더블탭(이동이 거의 없는 연속 탭) → 확대 토글
    if (Math.abs(dx) < 10 && Math.abs(dy) < 10) {
      const now = Date.now();
      if (now - lastTapRef.current < DOUBLE_TAP_MS) {
        setZoom((z) => !z);
        lastTapRef.current = 0;
      } else {
        lastTapRef.current = now;
      }
      return;
    }
    if (zoom) return; // 확대 중에는 스와이프로 넘기지 않는다
    if (dy > SWIPE_DOWN_CLOSE && Math.abs(dy) > Math.abs(dx)) {
      close();
      return;
    }
    if (Math.abs(dx) > SWIPE_X && Math.abs(dx) > Math.abs(dy)) step(dx < 0 ? 1 : -1);
  };

  if (!current) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="사진 크게 보기"
      className="fixed inset-0 z-modal flex flex-col text-white"
      style={{ background: 'var(--shell-on-surface)' }}
    >
      <div className="flex shrink-0 items-center px-03 pt-06">
        <button
          type="button"
          onClick={close}
          aria-label="닫기"
          className="flex h-09 w-09 items-center justify-center text-white"
        >
          <IconClose width={24} height={24} />
        </button>
        <div className="flex-1 text-center text-body-sm font-bold">
          {index + 1} / {total}
        </div>
        {onDownload ? (
          <button
            type="button"
            onClick={() => onDownload(index)}
            aria-label="사진 저장"
            className="flex h-09 w-09 items-center justify-center text-white"
          >
            <DownloadIcon />
          </button>
        ) : (
          <div className="h-09 w-09" aria-hidden="true" />
        )}
      </div>

      <div
        className="relative flex flex-1 touch-none select-none items-center justify-center overflow-hidden"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <button
          type="button"
          aria-label="이전 사진"
          disabled={index === 0}
          onClick={() => step(-1)}
          className="absolute left-03 top-1/2 z-base flex h-08 w-08 -translate-y-1/2 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/25 disabled:opacity-20"
        >
          <Chevron dir="left" />
        </button>

        <div
          className={['relative aspect-[4/3] w-full transition-transform duration-300', zoom ? 'scale-[2] cursor-zoom-out' : 'cursor-zoom-in'].join(' ')}
          onDoubleClick={() => setZoom((z) => !z)}
        >
          {current.thumb && (
            <CustomerImg
              src={current.thumb}
              alt=""
              aria-hidden="true"
              draggable={false}
              className={['absolute inset-0 h-full w-full object-contain transition-[filter] duration-300', loaded ? '' : 'blur-md'].join(' ')}
            />
          )}
          {current.src && (
            <CustomerImg
              key={current.src}
              src={current.src}
              alt={current.caption}
              draggable={false}
              onLoad={() => setLoaded(true)}
              className={['absolute inset-0 h-full w-full object-contain transition-opacity duration-300', loaded ? 'opacity-100' : 'opacity-0'].join(' ')}
            />
          )}
        </div>

        {!loaded && current.src && (
          <div className="absolute bottom-04 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-black/45 px-04 py-02 text-caption text-white/70">
            원본 불러오는 중…
          </div>
        )}

        <button
          type="button"
          aria-label="다음 사진"
          disabled={index === total - 1}
          onClick={() => step(1)}
          className="absolute right-03 top-1/2 z-base flex h-08 w-08 -translate-y-1/2 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/25 disabled:opacity-20"
        >
          <Chevron dir="right" />
        </button>
      </div>

      <div className="min-h-09 shrink-0 px-05 pb-03 pt-04 text-caption font-bold text-white/90">
        {current.caption}
        {current.sub && <small className="mt-01 block text-caption font-medium text-white/50">{current.sub}</small>}
      </div>

      <div ref={stripRef} className="flex shrink-0 gap-03 overflow-x-auto px-05 pb-04 pt-03">
        {photos.map((p, i) => (
          <button
            key={i}
            type="button"
            data-active={i === index}
            aria-label={`${i + 1}번째 사진`}
            onClick={() => onIndexChange(i)}
            className={[
              'h-09 w-09 shrink-0 overflow-hidden rounded-md border-2 transition-opacity',
              i === index ? 'border-white opacity-100' : 'border-transparent opacity-40',
            ].join(' ')}
          >
            {(p.thumb ?? p.src) && <CustomerImg src={(p.thumb ?? p.src) as string} alt="" draggable={false} className="h-full w-full object-cover" />}
          </button>
        ))}
      </div>

      <div className="shrink-0 pb-06 text-center text-caption text-white/35">좌우로 넘기기 · 두 번 탭하여 확대 · 아래로 밀어 닫기</div>
    </div>
  );
}
